import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { getPaymentsMadeReportPdf, listPaymentsMade } from "../services/reports";
import { listFrequencies } from "../services/teacher";
import { listExtraconfigs, listEmoluments } from "../services/payment";
import { MONTHS, monthName, formatCurrencyMZN } from "../utils/paymentHelpers";
import MultiSelectField from "./shared/MultiSelectField";
import NotifyModal from "./NotifyModal";
import { levelKey } from "../utils/classesLabels";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);

// Each option maps to the backend's `type` query param plus whether the
// extras/taxas multi-select below should be shown and used as extraIds/taxIds.
const TYPE_OPTIONS = [
  { value: "propinas", type: "" },
  { value: "extrasTodos", type: "extra" },
  { value: "extrasEspecificos", type: "extra", picksExtras: true },
  { value: "propinasExtras", type: "all" },
  { value: "taxasTodas", type: "tax" },
  { value: "taxasEspecificas", type: "tax", picksTaxas: true },
];

const PagamentosEfectuadosLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [year, setYear] = useState(currentYear);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [typeOption, setTypeOption] = useState("propinas");
  const [courseId, setCourseId] = useState("");
  const [frequencies, setFrequencies] = useState([]);
  const [extras, setExtras] = useState([]);
  const [taxas, setTaxas] = useState([]);
  const [selectedExtraIds, setSelectedExtraIds] = useState([]);
  const [selectedTaxIds, setSelectedTaxIds] = useState([]);
  const [allMonthlyPaid, setAllMonthlyPaid] = useState(false);
  const [generating, setGenerating] = useState(false);

  // On-screen "Consultar" list — same filters as the PDF, but fetched as JSON
  // (services/reports.js#listPaymentsMade) so students can be selected and
  // notified. `students` stays null until the user consults at least once,
  // so the table/selection bar don't render on first load.
  const [students, setStudents] = useState(null);
  const [consulting, setConsulting] = useState(false);
  const [selectedRegistrationIds, setSelectedRegistrationIds] = useState([]);
  const [notifyTargets, setNotifyTargets] = useState([]);

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
    listExtraconfigs().then((resp) => setExtras(resp.data || [])).catch(() => setExtras([]));
    listEmoluments().then((resp) => setTaxas(resp.data || [])).catch(() => setTaxas([]));
  }, []);

  const selectedType = TYPE_OPTIONS.find((o) => o.value === typeOption) || TYPE_OPTIONS[0];

  const currentFilters = () => ({
    year,
    months: selectedMonths,
    type: selectedType.type,
    courseId,
    extraIds: selectedType.picksExtras ? selectedExtraIds : undefined,
    taxIds: selectedType.picksTaxas ? selectedTaxIds : undefined,
    allMonthlyPaid,
  });

  const handleConsultar = async () => {
    setConsulting(true);
    try {
      const resp = await listPaymentsMade(currentFilters());
      setStudents(resp.data?.students || []);
      setSelectedRegistrationIds([]);
    } catch (err) {
      toast.error(t("pagamentosEfectuados.error.list"));
    } finally {
      setConsulting(false);
    }
  };

  const toggleSelect = (registrationId) => {
    setSelectedRegistrationIds((ids) =>
      ids.includes(registrationId) ? ids.filter((id) => id !== registrationId) : [...ids, registrationId]
    );
  };

  const toggleSelectAll = () => {
    if (!students || students.length === 0) return;
    setSelectedRegistrationIds((ids) =>
      ids.length === students.length ? [] : students.map((s) => s.registrationId)
    );
  };

  const openNotifySelected = () => {
    const targets = (students || []).filter((s) => selectedRegistrationIds.includes(s.registrationId));
    if (targets.length === 0) return;
    setNotifyTargets(targets);
  };

  const notifyPairs = useMemo(
    () => notifyTargets.map((s) => ({ registrationId: s.registrationId, contact: s.contact })),
    [notifyTargets]
  );

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const resp = await getPaymentsMadeReportPdf(currentFilters());
      const monthsLabel = selectedMonths.length ? selectedMonths.map(monthName).join(", ") : year;
      navigate("/documentos/pdf", {
        state: {
          url: resp.data.pdf,
          title: t("pagamentosEfectuados.documento.title", { period: monthsLabel }),
          filename: `pagamentos-efectuados-${year}.pdf`,
        },
      });
    } catch (err) {
      toast.error(t("pagamentosEfectuados.error.generate"));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='row gy-3'>
              <div className='col-sm-2'>
                <label className='form-label'>{t("turma.field.academicYear")}</label>
                <select className='form-select' value={year} onChange={(e) => setYear(Number(e.target.value))}>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-sm-3'>
                <label className='form-label'>{t("relatorioDividas.field.type")}</label>
                <select className='form-select' value={typeOption} onChange={(e) => setTypeOption(e.target.value)}>
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {t(`pagamentosEfectuados.type.${o.value}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-sm-3'>
                <label className='form-label'>{t(levelKey("taxa.field.course"))}</label>
                <select className='form-select' value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                  <option value=''>{t(levelKey("taxa.field.course.allLevels"))}</option>
                  {frequencies.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className='col-sm-2 d-flex align-items-end'>
                <button
                  type='button'
                  className='btn border border-primary-600 text-primary-600 w-100'
                  onClick={handleConsultar}
                  disabled={consulting}
                >
                  {consulting ? t("global.loading") : (<><i className='ri-search-line' /> {t("pagamentosEfectuados.action.consult")}</>)}
                </button>
              </div>
              <div className='col-sm-2 d-flex align-items-end'>
                <button type='button' className='btn btn-primary-600 w-100' onClick={handleGenerate} disabled={generating}>
                  {generating ? t("global.loading") : (<><i className='ri-file-chart-line' /> {t("pagamentosEfectuados.action.generate")}</>)}
                </button>
              </div>

              {selectedType.picksExtras && (
                <div className='col-12'>
                  <label className='form-label'>{t("pagamentosEfectuados.field.extras")}</label>
                  <MultiSelectField
                    items={extras}
                    getOptionValue={(e) => e.id}
                    getOptionLabel={(e) => e.name}
                    selectedIds={selectedExtraIds}
                    onChange={setSelectedExtraIds}
                    placeholder={t("pagamentosEfectuados.field.extras.placeholder")}
                    noOptionsMessage='-'
                  />
                </div>
              )}

              {selectedType.picksTaxas && (
                <div className='col-12'>
                  <label className='form-label'>{t("pagamentosEfectuados.field.taxas")}</label>
                  <MultiSelectField
                    items={taxas}
                    getOptionValue={(tx) => tx.id}
                    getOptionLabel={(tx) => tx.taxName}
                    selectedIds={selectedTaxIds}
                    onChange={setSelectedTaxIds}
                    placeholder={t("pagamentosEfectuados.field.taxas.placeholder")}
                    noOptionsMessage='-'
                  />
                </div>
              )}

              <div className='col-12'>
                <div className='form-check'>
                  <input
                    id='allMonthlyPaidCheckbox'
                    type='checkbox'
                    className='form-check-input'
                    checked={allMonthlyPaid}
                    onChange={(e) => setAllMonthlyPaid(e.target.checked)}
                  />
                  <label htmlFor='allMonthlyPaidCheckbox' className='form-check-label'>
                    {t("pagamentosEfectuados.field.allMonthlyPaid")}
                  </label>
                </div>
              </div>

              <div className='col-12'>
                <label className='form-label'>{t("relatorioDividas.field.months")}</label>
                <MultiSelectField
                  items={MONTHS}
                  getOptionValue={(m) => Number(m.code)}
                  getOptionLabel={(m) => m.desc}
                  selectedIds={selectedMonths}
                  onChange={setSelectedMonths}
                  placeholder={t("relatorioDividas.field.months.placeholder")}
                  noOptionsMessage='-'
                />
                <span className='text-secondary-light text-sm'>{t("relatorioDividas.hint.allMonths")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {students !== null && (
        <div className='col-12'>
          <div className='card radius-12'>
            <div className='card-body'>
              <div className='d-flex flex-wrap align-items-center gap-12 p-12 radius-8 bg-primary-50 mb-16'>
                {selectedRegistrationIds.length > 0 && (
                  <>
                    <span className='text-sm fw-medium text-primary-600'>
                      {t("estudante.list.selected.count", { count: selectedRegistrationIds.length })}
                    </span>
                    <button
                      type='button'
                      className='btn btn-primary-600 btn-sm d-flex align-items-center gap-2'
                      data-bs-toggle='modal'
                      data-bs-target='#notifyPagamentosEfectuadosModal'
                      onClick={openNotifySelected}
                    >
                      <Icon icon='mage:message-notify' /> {t("estudante.list.action.notify")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-link btn-sm text-secondary-light'
                      onClick={() => setSelectedRegistrationIds([])}
                    >
                      {t("estudante.list.selected.clear")}
                    </button>
                  </>
                )}
                <span className='text-sm text-secondary-light ms-auto'>
                  {t("pagamentosEfectuados.list.count", { count: students.length })}
                </span>
              </div>

              <div className='table-responsive scroll-sm'>
                <table className='table bordered-table mb-0'>
                  <thead>
                    <tr>
                      <th scope='col' style={{ width: 36 }}>
                        <div className='form-check style-check d-flex align-items-center'>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            checked={students.length > 0 && selectedRegistrationIds.length === students.length}
                            onChange={toggleSelectAll}
                          />
                        </div>
                      </th>
                      <th scope='col'>{t("global.name")}</th>
                      <th scope='col'>{t(levelKey("taxa.field.course"))}</th>
                      <th scope='col'>{t("global.contact")}</th>
                      <th scope='col' className='text-end'>{t("pagamentosEfectuados.list.totalPaid")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.length === 0 && (
                      <tr>
                        <td colSpan={5} className='text-center text-secondary-light py-32'>
                          {t("pagamentosEfectuados.list.empty")}
                        </td>
                      </tr>
                    )}
                    {students.map((s) => (
                      <tr key={s.registrationId}>
                        <td>
                          <div className='form-check style-check d-flex align-items-center'>
                            <input
                              type='checkbox'
                              className='form-check-input'
                              checked={selectedRegistrationIds.includes(s.registrationId)}
                              onChange={() => toggleSelect(s.registrationId)}
                            />
                          </div>
                        </td>
                        <td className='fw-semibold text-primary-light'>{s.name}</td>
                        <td>{s.course || "-"}</td>
                        <td>{s.contact || "-"}</td>
                        <td className='text-end'>{formatCurrencyMZN(s.totalAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      <NotifyModal
        modalId='notifyPagamentosEfectuadosModal'
        pairs={notifyPairs}
        count={notifyTargets.length}
        defaultType='Comunicado'
      />
    </div>
  );
};

export default PagamentosEfectuadosLayer;
